import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Navigation, Users, Calendar, Star, ArrowRight, Flame } from "lucide-react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";
import { useLiveStatus } from "../hooks/useLiveStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Countdown to Simhastha Kumbh 2028 — April 14, 2028
function Countdown() {
  const { t } = useLanguage();
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    const target = new Date("2028-04-14T00:00:00+05:30");
    const tick = () => {
      const diff = target - new Date();
      if (diff <= 0) return;
      setTime({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const units = [
    { label: t.time.days, value: time.days },
    { label: t.time.hours, value: time.hours },
    { label: t.time.minutes, value: time.minutes },
    { label: t.time.seconds, value: time.seconds },
  ];

  return (
    <div className="flex items-center gap-1.5 sm:gap-3 md:gap-5" data-testid="countdown-timer">
      {units.map(({ label, value }, i) => (
        <div key={label} className="flex items-center gap-1.5 sm:gap-3 md:gap-5">
          <div className="text-center">
            <motion.div
              key={value}
              initial={{ rotateX: 90, opacity: 0 }}
              animate={{ rotateX: 0, opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="font-heading font-bold text-2xl sm:text-4xl md:text-5xl text-white leading-none"
              style={{ textShadow: "0 0 20px rgba(255,153,51,0.4)" }}
            >
              {String(value).padStart(2, "0")}
            </motion.div>
            <div className="text-white/40 text-[10px] sm:text-xs uppercase tracking-widest mt-1 font-body">{label}</div>
          </div>
          {i < 3 && <span className="text-saffron text-xl sm:text-2xl font-bold opacity-60 -mt-3">:</span>}
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  const { t } = useLanguage();
  const [stats2, setStats2] = useState({ total_registrations: 0 });

  const features = [
    { icon: MapPin, title: t.loc.title, desc: t.loc.subtitle, href: "/locations", color: "#FF9933" },
    { icon: Users, title: t.reg.title, desc: t.reg.subtitle, href: "/register", color: "#FF5500" },
    { icon: Calendar, title: t.about.title, desc: t.about.subtitle, href: "/about", color: "#FFD700" },
  ];

  const stats = [
    { value: "5 Crore+", label: t.stats.pilgrims, icon: Users },
    { value: "30+ Days", label: t.stats.gathering, icon: Calendar },
    { value: "12", label: t.stats.akhadas, icon: Star },
    { value: "2028", label: t.stats.year, icon: Flame },
  ];

  const bathingDates = [
    { date: "14 Apr 2028", event: "Baisakhi Purnima — Main Shahi Snan", highlight: true },
    { date: "22 Apr 2028", event: "Akshaya Tritiya — Second Shahi Snan", highlight: false },
    { date: "27 Apr 2028", event: "Vaisakh Purnima — Third Shahi Snan", highlight: true },
    { date: "06 May 2028", event: "Shani Jayanti — Purnima Snan", highlight: false },
    { date: "14 May 2028", event: "Vaishakha Purnima — Closing Snan", highlight: false },
  ];

  const { data: live } = useLiveStatus();

  useEffect(() => {
    axios.get(`${API}/stats`).then(r => setStats2(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-stone-900 overflow-x-hidden">
      {live?.enabled && live.live && (
        <Link
          to="/live"
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 pl-3 pr-4 py-2.5 rounded-full max-w-[92vw] hover:scale-[1.03] transition-transform"
          style={{ background: "rgba(127,29,29,0.7)", backdropFilter: "blur(18px) saturate(160%)", WebkitBackdropFilter: "blur(18px) saturate(160%)", border: "1px solid rgba(248,113,113,0.35)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}
          data-testid="home-live-banner"
        >
          <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
          </span>
          <span className="text-white text-sm font-body truncate">{live.live.title}</span>
          <span className="text-red-200 text-xs font-semibold flex-shrink-0">Watch <ArrowRight size={12} className="inline" /></span>
        </Link>
      )}
      {/* Disclaimer Banner (glass) */}
      <div
        className="py-2.5 px-4 text-center relative z-20"
        style={{
          backgroundColor: "rgba(120,53,15,0.6)",
          backgroundImage:
            "linear-gradient(90deg, rgba(120,53,15,0.55) 0%, rgba(146,64,14,0.55) 50%, rgba(120,53,15,0.55) 100%)",
          backdropFilter: "blur(14px) saturate(160%)",
          WebkitBackdropFilter: "blur(14px) saturate(160%)",
          borderBottom: "1px solid rgba(251,191,36,0.2)",
          boxShadow: "inset 0 -1px 0 rgba(255,255,255,0.03)",
        }}
        data-testid="disclaimer-banner"
      >
        <p className="text-amber-100/95 text-xs sm:text-sm font-body">
          <span className="font-semibold">{t.disclaimer.notice}:</span> {t.disclaimer.text}
        </p>
      </div>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1658730458768-8b8cc0c00955?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxNYWhha2FsZXNod2FyJTIwVGVtcGxlJTIwVWpqYWlufGVufDB8fHx8MTc3Mjg5OTU0MHww&ixlib=rb-4.1.0&q=85&w=1920"
            alt="Mahakaleshwar Temple Ujjain"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-hero-overlay" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(255,153,51,0.12), transparent 60%)" }} />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-32 pb-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-saffron/30 bg-saffron/10 text-saffron text-sm font-body font-medium mb-8">
              <Flame size={14} />
              {t.hero.badge}
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }}
            className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl text-white leading-tight mb-4"
          >
            {t.hero.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
            className="font-body text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-12"
            style={{ textShadow: "0 2px 10px rgba(0,0,0,0.9)" }}
          >
            {t.hero.subtitle}
          </motion.p>

          {/* Countdown + CTA glass card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mx-auto max-w-3xl rounded-3xl px-4 sm:px-8 py-8 sm:py-10 flex flex-col items-center gap-6"
            style={{
              background: "rgba(28,28,30,0.35)",
              backdropFilter: "blur(20px) saturate(150%)",
              WebkitBackdropFilter: "blur(20px) saturate(150%)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow:
                "0 25px 80px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
            data-testid="hero-glass-card"
          >
            <div className="flex flex-col items-center gap-3 w-full">
              <p className="text-white/50 text-xs sm:text-sm uppercase tracking-widest font-body">
                {t.hero.begins_in}
              </p>
              <Countdown />
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full pt-2">
              <Link
                to="/register"
                data-testid="hero-register-btn"
                className="w-full sm:w-auto px-8 py-3.5 rounded-full font-body font-semibold bg-saffron text-stone-900 hover:bg-saffron-light transition-all hover:shadow-[0_0_25px_rgba(255,153,51,0.5)] active:scale-95 flex items-center justify-center gap-2"
              >
                {t.hero.register_now} <ArrowRight size={16} />
              </Link>
              <Link
                to="/locations"
                data-testid="hero-explore-btn"
                className="w-full sm:w-auto px-8 py-3.5 rounded-full font-body font-medium border border-white/15 text-white/85 hover:bg-white/[0.06] hover:border-saffron/30 transition-all text-center"
                style={{ backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}
              >
                {t.hero.explore_btn}
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 w-6 h-10 rounded-full border border-white/20 flex items-start justify-center pt-2">
          <div className="w-1 h-2 rounded-full bg-saffron" />
        </motion.div>
      </section>

      {/* Stats Bar */}
      <section className="py-10 border-y border-white/5" style={{ background: "rgba(44,44,46,0.5)" }} data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map(({ value, label, icon: Icon }, i) => (
            <motion.div key={label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }} viewport={{ once: true }}
              className="flex flex-col items-center text-center gap-1">
              <Icon size={20} className="text-saffron mb-1 opacity-80" />
              <div className="font-heading font-bold text-2xl sm:text-3xl text-white">{value}</div>
              <div className="text-white/40 text-xs uppercase tracking-widest font-body">{label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="py-24 relative radial-top-glow" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <p className="text-saffron text-sm uppercase tracking-widest font-body mb-3">Plan Your Journey</p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white">Everything You Need</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map(({ icon: Icon, title, desc, href, color }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                whileHover={{ y: -4 }} className="group">
                <Link to={href} className="block h-full glass-card rounded-2xl p-6 hover:border-saffron/20 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
                    style={{ background: `rgba(${color === "#FF9933" ? "255,153,51" : color === "#FFD700" ? "255,215,0" : "255,85,0"},0.15)` }}>
                    <Icon size={22} style={{ color }} />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-white mb-2">{title}</h3>
                  <p className="font-body text-sm text-white/50 leading-relaxed">{desc}</p>
                  <div className="flex items-center gap-1 mt-4 text-saffron text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Explore <ArrowRight size={12} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bathing Dates */}
      <section className="py-20 bg-stone-800/40" data-testid="bathing-dates-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <p className="text-saffron text-sm uppercase tracking-widest font-body mb-3">Sacred Calendar</p>
            <h2 className="font-heading font-bold text-4xl text-white">Shahi Snan Dates</h2>
            <p className="text-white/40 font-body mt-3">The most auspicious royal bathing dates of Simhastha 2028</p>
          </motion.div>
          <div className="space-y-3">
            {bathingDates.map(({ date, event, highlight }, i) => (
              <motion.div key={date} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }} viewport={{ once: true }}
                className={`flex items-center gap-5 p-4 rounded-xl transition-all ${highlight ? "border border-saffron/30 bg-saffron/5" : "border border-white/5 bg-white/2"}`}>
                <div className={`w-2 h-2 rounded-full shrink-0 ${highlight ? "bg-saffron" : "bg-white/20"}`} />
                <div className="font-heading font-semibold text-sm text-saffron min-w-[110px]">{date}</div>
                <div className={`font-body text-sm ${highlight ? "text-white" : "text-white/60"}`}>{event}</div>
                {highlight && <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-saffron/20 text-saffron font-body">Main</span>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hero image 2 */}
      <section className="py-20" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <p className="text-saffron text-sm uppercase tracking-widest font-body mb-4">The Holy City</p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6">
                Mahakal ki Nagri<br />
                <span className="text-gradient-saffron">Ujjain</span>
              </h2>
              <p className="font-body text-white/60 text-base leading-relaxed mb-6">
                Ujjain — the city of Mahakaleshwar, one of the twelve Jyotirlingas — sits on the banks of the holy Kshipra river. Every 12 years, the Simhastha Kumbh Mela transforms this ancient city into the spiritual capital of the world.
              </p>
              <p className="font-body text-white/50 text-base leading-relaxed mb-8">
                In 2028, millions of devotees will gather here for the sacred Shahi Snan, seeking liberation from the cycle of rebirth.
              </p>
              <Link to="/about" data-testid="learn-more-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-saffron/30 text-saffron hover:bg-saffron/10 transition-all font-body font-medium">
                Learn More <ArrowRight size={16} />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden aspect-[4/3]">
              <img src="https://images.pexels.com/photos/34696655/pexels-photo-34696655.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Sadhu at Kumbh" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <p className="font-body text-white/60 text-xs uppercase tracking-widest">Est. Pilgrims 2028</p>
                <p className="font-heading text-3xl text-white font-bold">5 Crore+</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="glass-card rounded-3xl p-12" style={{ border: "1px solid rgba(255,153,51,0.15)" }}>
            <div className="w-16 h-16 rounded-full bg-saffron/10 flex items-center justify-center mx-auto mb-6">
              <Flame size={28} className="text-saffron" />
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              {t.cta.title}
            </h2>
            <p className="font-body text-white/50 mb-8">
              {t.cta.subtitle}
            </p>
            {stats2.total_registrations > 0 && (
              <p className="font-body text-saffron/80 text-sm mb-6">{stats2.total_registrations.toLocaleString()} {t.cta.registered}</p>
            )}
            <Link to="/register" data-testid="cta-register-btn"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-body font-semibold bg-saffron text-stone-900 hover:shadow-[0_0_30px_rgba(255,153,51,0.5)] transition-all active:scale-95">
              {t.cta.btn} <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
